import { useCadModelObjectLimitedBoundingBox } from "@/hooks/use-object-bounding-box";
import { useCached3DObject } from "@/object-cache";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { selectSheetCadAlignmentStep } from "@/store/modes/sheet-cad-alignment-mode-selectors";
import { SheetCadAlignmentStep } from "@/store/modes/sheet-cad-alignment-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import { useEffect, useMemo, type JSX } from "react";
import { Box3 } from "three/src/math/Box3";
import { useSheetSelectedForAlignment } from "../mode-data-context";
import { AlignSheetCadScene } from "./align-sheet-cad-scene";
import { SetHeightScene } from "./set-height-scene";

/** @returns the scene for the sheet and cad alignment mode */
export function SheetCadAlignmentModeScene(): JSX.Element {
  const sheet = useSheetSelectedForAlignment("sheetAndCad");
  const activeCad = useAppSelector(selectActiveCadModel);
  assert(activeCad, "Sheet and CAD Alignment Mode requires a CAD model");

  const cadModel = useCached3DObject(activeCad, "SheetCadAlignmentModeScene");

  // get the cad model bounding box in world
  const cadBox = useCadModelObjectLimitedBoundingBox(
    cadModel,
    cadModel.iElement.id,
  );
  const cadBoundingBox = useMemo(
    () => cadBox.boundingBox ?? new Box3(),
    [cadBox.boundingBox],
  );

  useEffect(() => {
    // reset any highlight state from the cadModel
    for (const objectId of cadModel.objectIds) {
      cadModel.setObjectHighlighted(objectId, false);
    }
  }, [cadModel]);

  const step = useAppSelector(selectSheetCadAlignmentStep);
  switch (step) {
    case SheetCadAlignmentStep.setElevation:
      return <SetHeightScene activeCad={activeCad} cadBox={cadBoundingBox} />;
    case SheetCadAlignmentStep.alignIn2D:
      return (
        <AlignSheetCadScene
          activeSheet={sheet}
          activeCad={activeCad}
          cadBox={cadBoundingBox}
        />
      );
  }
}
